import { css } from '@emotion/react'
import { Button, Cell, FormControl, Grid, Heading, HFlow, Icon, Text } from 'bold-ui'
import { useErrorHandler } from 'components/error/PromiseErrorBoundary'
import { useAtividadeColetivaGrupoDetalhesLazyQuery } from 'graphql/hooks.generated'
import { TipoGrupoParticipanteAtividadeColetivaEnum } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { useConfirmRemoverGrupo } from '../grupos/hooks/useConfirmRemoverGrupo'
import { getParamsGrupo } from '../utils-atividadeColetivaAnalytics'
import {
  AtividadeColetivaGrupoComIntegrantesModel,
  AtividadeColetivaGrupoItemModel,
  AtividadeColetivaGruposSelectField,
} from './AtividadeColetivaGrupoSelectField'
import { convertToAtividadeColetivaGrupoComIntegrantesModel } from './AtividadeColetivaGrupoSelectField/converter-atividadeColetivaGruposSelect'

type AtividadeColetivaGruposFieldProps = {
  name: MetaPath<AtividadeColetivaGrupoComIntegrantesModel | null>
  tipoGrupo: TipoGrupoParticipanteAtividadeColetivaEnum
  onAdicionarGrupo(value: AtividadeColetivaGrupoComIntegrantesModel): void
  onRemoverGrupo(value: AtividadeColetivaGrupoComIntegrantesModel): void
}

export default function AtividadeColetivaGruposField(props: Readonly<AtividadeColetivaGruposFieldProps>) {
  const { analytics } = useAnalytics()

  const { name, tipoGrupo, onAdicionarGrupo, onRemoverGrupo } = props
  const selectedGrupoPrefix = 'new-'
  const confirmRemoverGrupo = useConfirmRemoverGrupo()
  const handleRejection = useErrorHandler()
  const [
    carregarIntegrantesDoGrupo,
    { loading: isCarregandoIntegrantes },
  ] = useAtividadeColetivaGrupoDetalhesLazyQuery()

  const {
    input: { value: addedGrupo, onChange: setAddedGrupo },
  } = useField<AtividadeColetivaGrupoComIntegrantesModel | null>(name.absolutePath(), {
    subscription: { value: true },
  })

  const {
    input: { value: selectedGrupo, onChange: setSelectedGrupo },
  } = useField<AtividadeColetivaGrupoItemModel | null>(`${selectedGrupoPrefix}${name.absolutePath()}`, {
    subscription: { value: true },
  })

  const hasAddedGrupo = !!addedGrupo

  const handleAdicionarGrupo = () => {
    if (!selectedGrupo) return

    carregarIntegrantesDoGrupo({ variables: { grupoId: selectedGrupo.id } })
      .then((result) => {
        if (result.error || !result.data?.grupoParticipanteAtividadeColetiva) return

        const grupoComIntegrantes = convertToAtividadeColetivaGrupoComIntegrantesModel(selectedGrupo, result.data)

        onAdicionarGrupo(grupoComIntegrantes)

        analytics.logEvent(
          'atividade_coletiva_grupo_adicionado',
          getParamsGrupo(
            tipoGrupo,
            grupoComIntegrantes.cidadaos?.length ?? 0,
            grupoComIntegrantes.lotacoes?.length ?? 0
          )
        )

        setAddedGrupo(grupoComIntegrantes)
        setSelectedGrupo(null)
      })
      .catch(handleRejection)
  }

  const handleRemoverGrupo = () => {
    if (!hasAddedGrupo) return

    confirmRemoverGrupo()
      .then((confirmResult) => {
        if (!confirmResult) return

        onRemoverGrupo(addedGrupo)

        analytics.logEvent('atividade_coletiva_grupo_removido', { tipo_grupo: tipoGrupo })

        setAddedGrupo(null)
        setSelectedGrupo(null)
      })
      .catch(handleRejection)
  }

  return (
    <Grid gap={1}>
      <Cell size={12}>
        <Heading level={2} style={styles.title}>
          Grupos
        </Heading>
        <Text>
          Selecione um grupo de atividade coletiva para importar automaticamente os participantes associados ao grupo
          para este registro. Alterações feitas na lista de participantes deste registro não impactam a configuração
          original do grupo. Participantes adicionais também podem ser inseridos manualmente.{' '}
          <strong>Importante:</strong> a funcionalidade de grupos está disponível somente na criação da atividade
          coletiva. Na edição, é possível apenas ajustar os participantes individualmente.
        </Text>
      </Cell>

      <Cell size={6}>
        <AtividadeColetivaGruposSelectField
          name={hasAddedGrupo ? name.absolutePath() : `${selectedGrupoPrefix}${name.absolutePath()}`}
          label='Grupos de atividade coletiva'
          tipoGrupo={tipoGrupo}
          disabled={hasAddedGrupo}
        />
      </Cell>

      <Cell size={3}>
        <FormControl label={<span>&nbsp;</span>}>
          {hasAddedGrupo ? (
            <Button kind='danger' skin='outline' size='small' onClick={handleRemoverGrupo}>
              <HFlow hSpacing={0.5}>
                <Icon icon='timesDefault' />
                Remover grupo
              </HFlow>
            </Button>
          ) : (
            <Button
              kind='primary'
              skin='outline'
              size='small'
              disabled={!selectedGrupo}
              loading={isCarregandoIntegrantes}
              onClick={handleAdicionarGrupo}
              data-testid='Button.adicionarGrupo'
            >
              Adicionar grupo
            </Button>
          )}
        </FormControl>
      </Cell>
    </Grid>
  )
}

const styles = {
  title: css`
    margin-bottom: 0.5rem;
  `,
}
